import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/// Campo de direcciones de correo en forma de pastillas.
///
/// Sustituye al campo de texto con direcciones separadas por comas: ahí un espacio de más o una coma que falta se
/// convierten en un correo que no sale, y no se ve hasta que falla el envío. Con pastillas cada dirección se valida
/// al confirmarla y las que no valen se marcan en rojo.
///
/// Acepta pegar un bloque entero separado por comas, puntos y comas, saltos de línea o tabuladores, que es lo que
/// sale al copiar destinatarios desde Outlook.
@Component({
    selector: 'app-address-chips',
    templateUrl: './address-chips.component.html',
    styleUrls: ['./address-chips.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AddressChipsComponent), multi: true }
    ],
    standalone: false
})
export class AddressChipsComponent implements ControlValueAccessor {
  @Input() placeholder: string = '';

  public addresses: string[] = [];
  public draft: string = '';
  public disabled: boolean = false;

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  /// Validación deliberadamente laxa: solo descarta lo que seguro no es una dirección. El servidor de correo es
  /// quien decide de verdad, y una expresión estricta rechazaría direcciones válidas poco frecuentes.
  private static readonly PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/;

  writeValue(value: string[] | string | null) {
    if (Array.isArray(value)) this.addresses = [...value];
    else if (typeof value === 'string') this.addresses = this.split(value);
    else this.addresses = [];
  }

  registerOnChange(fn: (value: string[]) => void) { this.onChange = fn; }
  registerOnTouched(fn: () => void) { this.onTouched = fn; }
  setDisabledState(disabled: boolean) { this.disabled = disabled; }

  isValid(address: string): boolean {
    return AddressChipsComponent.PATTERN.test(address);
  }

  get hasInvalid(): boolean {
    return this.addresses.some(address => !this.isValid(address));
  }

  /// Confirma lo escrito. Se llama con Enter, coma, punto y coma, tabulador y al perder el foco.
  commit() {
    const nuevas = this.split(this.draft);
    if (nuevas.length > 0) {
      for (const address of nuevas) {
        if (!this.addresses.includes(address)) this.addresses.push(address);
      }
      this.draft = '';
      this.emit();
    }
    this.onTouched();
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';' || event.key === 'Tab') {
      //con el campo vacío el tabulador tiene que seguir moviendo el foco
      if (event.key === 'Tab' && !this.draft.trim()) return;
      event.preventDefault();
      this.commit();
      return;
    }

    //retroceso con el campo vacío quita la última pastilla, como en cualquier campo de este tipo
    if (event.key === 'Backspace' && !this.draft && this.addresses.length > 0) {
      this.addresses.pop();
      this.emit();
    }
  }

  onPaste(event: ClipboardEvent) {
    const texto = event.clipboardData?.getData('text') ?? '';
    if (!texto) return;
    event.preventDefault();
    this.draft = this.draft + texto;
    this.commit();
  }

  remove(address: string) {
    this.addresses = this.addresses.filter(item => item !== address);
    this.emit();
  }

  private split(value: string): string[] {
    return (value ?? '')
      .split(/[,;\n\t]+/)
      .map(part => part.trim())
      //se admite el formato "Nombre <correo>" que sale al copiar de Outlook: nos quedamos con la dirección
      .map(part => {
        const match = part.match(/<([^>]+)>\s*$/);
        return match ? match[1].trim() : part;
      })
      .filter(part => part.length > 0);
  }

  private emit() {
    this.onChange([...this.addresses]);
  }
}
